import QueryString from 'qs';

// NCP-5636: the broker portal's New Quote link carries the agency/broker
// attribution in the query string only. Any way back to a bare `/` in the same
// tab — the Foxquilt logo is `<Link to="/">` — dropped it, and the account was
// filed under the Foxquilt agency with no broker. Remember the attribution the
// tab was opened with and put it back when `/` arrives without any.
//
// sessionStorage, not localStorage: each Start Application opens its own tab,
// so two quotes for two partners side by side keep their own attribution, and
// a link opened last week can never attach to today's quote.
const AGENCY_ATTRIBUTION_KEY = 'agencyAttribution';

export const AGENCY_ATTRIBUTION_KEYS = [
  'agencyId',
  'agencyBrokerId',
  'foxquiltAgencyId',
  'foxquiltBrokerId',
  'partnercode',
  'brokerCode'
] as const;

type AgencyAttribution = Partial<
  Record<typeof AGENCY_ATTRIBUTION_KEYS[number], string>
>;

const pickAttribution = (query: QueryString.ParsedQs): AgencyAttribution => {
  const attribution: AgencyAttribution = {};
  for (const key of AGENCY_ATTRIBUTION_KEYS) {
    const value = query[key];
    if (typeof value === 'string' && value.trim() !== '') {
      attribution[key] = value;
    }
  }
  return attribution;
};

const readStored = (): AgencyAttribution => {
  try {
    const stored = sessionStorage.getItem(AGENCY_ATTRIBUTION_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : null;
    return parsed && typeof parsed === 'object'
      ? pickAttribution(parsed as QueryString.ParsedQs)
      : {};
  } catch {
    return {};
  }
};

const store = (attribution: AgencyAttribution) => {
  try {
    sessionStorage.setItem(AGENCY_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Storage blocked (private mode, quota): the URL still works on its own.
  }
};

/**
 * Remembers the query's attribution, or fills it in from the tab's last one.
 *
 * A query carrying ANY attribution replaces the remembered one whole — mixing
 * the two could pair one agency with another agency's broker (the same rule
 * forms-backend's resolveAgencyAttribution applies). Endorsement and renewal
 * links are left alone: they belong to an existing policy, not the partner
 * this tab last quoted for.
 */
export function withAgencyAttribution(
  query: QueryString.ParsedQs
): QueryString.ParsedQs {
  // Any attribution key at all, even one that fails validation, means the
  // query speaks for itself: the page's own checks see it as it came.
  if (AGENCY_ATTRIBUTION_KEYS.some((key) => key in query)) {
    store(pickAttribution(query));
    return query;
  }
  if (query.endorsement || query.renewal) {
    return query;
  }
  return { ...query, ...readStored() };
}
